/**
 * Guest join — /apps/session/join/:token
 *
 * The consumer promise made true: someone opens a link, types their name, and
 * weighs in. No account, no password, no "contact the host for access".
 *
 * After joining, a guest who is not signed in answers at the room's own
 * address, `/prioritization/sessions/:id/live`, the same address a member
 * lands on (roadmap S-8, ruling R1); the guest pass lets that page run for
 * them. Someone signed in who joins by a guest link answers here, on the same
 * guest screen (GuestRoomScreen), since the room's address would open the
 * member's room. Opening the link again goes straight back to the room.
 *
 * Public by construction — this route is in RequireAuth's PUBLIC_PREFIXES, and
 * every write goes through a token-scoped SECURITY DEFINER RPC. A guest can
 * contribute a response and nothing else; authority stays with authenticated
 * humans (build contract §2.3a / P-AGENT-1).
 */

import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  findGuestPassForToken,
  guestRoomPath,
  joinAsGuest,
  saveGuestPass,
  type GuestPass,
} from '@/lib/guestParticipant';
import { useUserStore } from '@/stores/prioritization/userStore';
import { AppCard, Eyebrow, PrimaryButton } from '../components/primitives';
import GuestRoomScreen from './GuestRoomScreen';

export default function GuestJoinScreen() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const signedIn = Boolean(userId) && userId !== 'unknown';
  const [name, setName] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pass, setPass] = useState<GuestPass | null>(() => (token ? findGuestPassForToken(token) : null));

  const canJoin = name.trim().length > 0 && !joining && Boolean(token);

  const join = async () => {
    if (!token) return;
    setJoining(true);
    setError(null);
    const result = await joinAsGuest(token, name);
    setJoining(false);
    if (!result.ok || !result.data) {
      setError(result.error ?? 'Could not join.');
      return;
    }
    saveGuestPass(token, result.data);
    if (!signedIn) {
      navigate(guestRoomPath(result.data.sessionId), { replace: true });
      return;
    }
    setPass({ ...result.data, token });
  };

  if (pass && !signedIn) return <Navigate to={guestRoomPath(pass.sessionId)} replace />;
  if (pass) return <GuestRoomScreen pass={pass} />;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 pt-[env(safe-area-inset-top)] dark:bg-[var(--bg)]">
      <main className="mx-auto w-full max-w-md flex-1 px-4 py-10">
        <Eyebrow>Priorities.ai</Eyebrow>

        <h1 className="mt-2 font-heading text-2xl font-semibold text-gray-900 dark:text-[var(--text-pri)]">
          Join the session
        </h1>
        <p className="mt-1 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
          Add your name so people can see who weighed in. That&apos;s all — no account, no
          password.
        </p>

        <AppCard className="mt-5">
          <label
            className="block font-body text-xs text-gray-500 dark:text-[var(--text-ter)]"
            htmlFor="guest-name"
          >
            Your name
          </label>
          <input
            id="guest-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canJoin) void join();
            }}
            autoFocus
            placeholder="e.g. Priya"
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-base text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          />

          {error ? (
            <p className="mt-3 font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
              {error}
            </p>
          ) : null}

          <div className="mt-4">
            <PrimaryButton onClick={() => void join()} disabled={!canJoin} className="w-full">
              {joining ? 'Joining…' : 'Join'} <ArrowRight className="h-4 w-4" />
            </PrimaryButton>
          </div>
        </AppCard>

        <p className="mt-4 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
          Your name is the only thing recorded. You can weigh in — the host runs the session.
        </p>
      </main>
    </div>
  );
}
